import { useState, useEffect, useRef, useCallback } from 'react';

type SidebarStyle = {
  position?: 'static' | 'sticky';
  top?: string;
  transform?: string;
  transition?: string;
};

type UseStickysidebar = {
  sidebarRef: React.RefObject<HTMLElement>;
  sidebarStyle: SidebarStyle;
  isOverflowing: boolean;
  isStickyDisabled: boolean;
};

type UseStickySidebarOptions = {
  disableSticky?: boolean;
};

/**
 * Custom hook for managing sticky sidebar with snap-to-direction behavior
 * When sidebar overflows: scroll down = snap to bottom, scroll up = snap to top
 * If viewport is too small to allow 100% sidebar visibility with snap behavior,
 * sticky is disabled to allow normal scrolling.
 * @param options - Configuration options including disableSticky flag
 */
export function useStickySidebar(options: UseStickySidebarOptions = {}): UseStickysidebar {
  const { disableSticky = false } = options;
  const sidebarRef = useRef<HTMLElement>(null);
  const [sidebarStyle, setSidebarStyle] = useState<SidebarStyle>({});
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [isStickyDisabled, setIsStickyDisabled] = useState<boolean>(false);
  const lastScrollTop = useRef<number>(0);
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const originalTopRef = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    if (!sidebarRef.current || disableSticky) {
      // If sticky is disabled, reset to static positioning
      if (disableSticky) {
        setSidebarStyle({ position: 'static' });
      }
      return;
    }

    const sidebar = sidebarRef.current;
    const sidebarHeight = sidebar.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Store original position on first calculation when sidebar is not yet sticky
    if (originalTopRef.current === null) {
      const sidebarRect = sidebar.getBoundingClientRect();
      originalTopRef.current = sidebarRect.top + scrollTop;
    }

    const topOffset = 20;
    const hasIntersected = scrollTop >= originalTopRef.current - topOffset;

    // Detect scroll direction with minimum threshold to avoid jitter
    const scrollDiff = scrollTop - lastScrollTop.current;

    // Determine current direction from scroll difference
    let currentDirection = scrollDirection; // Default to stored direction

    // Only update direction if there's actual movement
    if (Math.abs(scrollDiff) > 1) {
      currentDirection = scrollDiff > 0 ? 'down' : 'up';

      if (currentDirection !== scrollDirection) {
        setScrollDirection(currentDirection);
      }
      lastScrollTop.current = scrollTop;
    }

    const availableHeight = viewportHeight - topOffset;

    // Check if sidebar content is taller than available viewport space
    const overflowing = sidebarHeight > availableHeight;
    setIsOverflowing(overflowing);

    // Check if viewport is large enough for the snap behavior to show 100% of sidebar content
    // With snap behavior: top view shows 0 to availableHeight, bottom view shows (sidebarHeight - availableHeight) to sidebarHeight
    // For full coverage, these ranges must overlap: availableHeight >= sidebarHeight - availableHeight
    // This means: sidebarHeight <= 2 * availableHeight
    const canShowFullSidebar = sidebarHeight <= 2 * availableHeight;
    const shouldDisableSticky = overflowing && !canShowFullSidebar;
    setIsStickyDisabled(shouldDisableSticky);

    if (!overflowing || !hasIntersected || shouldDisableSticky) {
      // If content fits in viewport, hasn't intersected yet, or viewport too small for full coverage
      // Use simple positioning (static when disabled, sticky otherwise)
      setSidebarStyle({
        position: hasIntersected && !shouldDisableSticky ? 'sticky' : 'static',
        top: hasIntersected && !shouldDisableSticky ? `${topOffset}px` : 'auto',
      });
      return;
    }

    // For overflowing content after intersection (when viewport can show full sidebar), snap to direction immediately
    const maxTranslate = sidebarHeight - availableHeight;
    let translateY = 0;

    // Use the current detected direction (not stored state)
    if (currentDirection === 'down') {
      // Scrolling down = move sidebar UP (show bottom content)
      translateY = -maxTranslate;
    } else {
      // Scrolling up = move sidebar DOWN (show top content)
      translateY = 0;
    }

    setSidebarStyle({
      position: 'sticky',
      top: `${topOffset}px`,
      transform: `translateY(${translateY}px)`,
      transition: 'transform 0.15s ease-out',
    });
  }, [scrollDirection, disableSticky]);

  const handleResize = useCallback(() => {
    handleScroll();
  }, [handleScroll]);

  useEffect(() => {
    // Initial calculation
    handleScroll();

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, handleResize]);

  return {
    sidebarRef,
    sidebarStyle,
    isOverflowing,
    isStickyDisabled,
  };
}
